import type { AgentStep } from "../types/chatWidget";

export const WINDOW_EDGE_GAP = 20;

/** How tall the window can be without leaving the screen, given where it opens from the trigger. */
export function getAvailableWindowHeight(
	trigger: DOMRect | undefined,
	viewportHeight: number,
	position?: string,
): number {
	const gap = 5 + 12; // The space between window and trigger, plus some room at the screen's edge
	if (!trigger || position?.startsWith("center")) return viewportHeight - 2 * 12;
	if (position?.startsWith("top")) return viewportHeight - trigger.bottom - gap;
	return trigger.top - gap;
}

export function getChatPosition(
	triggerPosition: DOMRect,
	Cwidth: number,
	Cheight: number,
	position?: string,
): {
	top: string; left: string;
	position?: string,
} {
	if (!triggerPosition) {
		return { top: "0px", left: "0px" }; // Return empty string if trigger position is not available
	}

	const { width, height } = triggerPosition;

	const distance = 5; // Adjust this value to set the desired distance from the trigger
	if (!position) return { top: distance + height + "px", left: width + "px" };

	switch (position) {
		case "top-left":
			return { top: distance + height + "px", left: "0px" };
		case "top-center":
			return { top: distance + height + "px", left: width / 2 - Cwidth / 2 + "px" };
		case "top-right":
			return { top: distance + height + "px", left: width - Cwidth + "px" };
		case "center-left":
			return { top: height / 2 - Cheight / 2 + "px", left: width + distance + "px" };
		case "center-right":
			return {
				top: height / 2 - Cheight / 2 + "px",
				left: -distance - Cwidth + "px",
			};
		case "bottom-right":
			return { top: -distance - Cheight + "px", left: width - Cwidth + "px" };
		case "bottom-center":
			return {
				top: -distance - Cheight + "px",
				left: width / 2 - Cwidth / 2 + "px",
			};
		case "bottom-left":
			return { top: -distance - Cheight + "px", left: "0px" };
		default:
			return { top: -distance - Cheight + "px", left: width - Cwidth + "px" };
	}
}

export function getAnimationOrigin(position?: string) {
	if (!position) return "origin-top-left";
	switch (position) {
		case "top-left":
			return "origin-top-left";
		case "top-center":
			return "origin-top";
		case "top-right":
			return "origin-top-right";
		case "center-left":
			return "origin-left";
		case "center-right":
			return "origin-right";
		case "bottom-right":
			return "origin-bottom-right";
		case "bottom-center":
			return "origin-bottom";
		case "bottom-left":
			return "origin-bottom-left";
		default:
			return "origin-bottom-right";
	}
}

function extractText(value: any): string {
	if (typeof value === "string") return value;
	if (!value || typeof value !== "object") return "";

	if (typeof value.message === "string") return value.message;
	if (typeof value.text === "string") return value.text;
	if (typeof value.data?.text === "string") return value.data.text;
	if (typeof value.data?.message === "string") return value.data.message;
	if (typeof value.data?.message?.text === "string") return value.data.message.text;
	if (typeof value.data?.message?.data?.text === "string") return value.data.message.data.text;
	if (typeof value.results?.message?.text === "string") return value.results.message.text;
	if (typeof value.results?.message?.data?.text === "string") return value.results.message.data.text;

	return "";
}

export function extractMessageFromOutput(output: any): string {
	if (!output) return "";

	const direct = extractText(output);
	if (direct) return direct;

	if (output.type === "text" || output.type === "message" || output.type === "object") {
		const typedMessage = extractText(output.message);
		if (typedMessage) return typedMessage;
	}

	const outputContainers = [
		output.result?.outputs,
		output.outputs,
		output.data?.outputs,
	];

	for (const container of outputContainers) {
		if (Array.isArray(container)) {
			for (const runOutput of container) {
				const nestedOutputs = runOutput?.outputs;
				if (Array.isArray(nestedOutputs)) {
					for (const nested of nestedOutputs) {
						const text = extractMessageFromOutput(nested);
						if (text) return text;
					}
				}
				const text = extractText(runOutput) || extractMessageFromOutput(runOutput?.results);
				if (text) return text;
			}
		} else if (container && typeof container === "object") {
			const preferred = extractMessageFromOutput(container.message);
			if (preferred) return preferred;

			for (const value of Object.values(container)) {
				const text = extractMessageFromOutput(value);
				if (text) return text;
			}
		}
	}

	const artifactsText = extractText(output.artifacts?.message);
	if (artifactsText) return artifactsText;

	return "";
}

/**
 * Derive a human-readable component label from a raw vertex ID.
 * Vertex IDs have the format "ComponentType-RandomSuffix", e.g. "ChatInput-iRn2I".
 * We strip the suffix and insert spaces before capitals:
 *   "ChatInput"    → "Chat Input"
 *   "OpenAIModel"  → "Open AI Model"
 *   "Memory"       → "Memory"
 */
function deriveComponentLabel(nodeId: string): string {
	// Strip the random suffix (last hyphen + alphanumerics)
	const withoutSuffix = nodeId.replace(/-[A-Za-z0-9]+$/, "");
	// Insert space before each uppercase letter following a lowercase letter
	return withoutSuffix.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
}

export function agentStepFromNodeEvent(
	data: any,
	runId: string,
	status: "running" | "completed",
): AgentStep | null {
	const buildData = data?.build_data ?? data;
	const componentDisplayName =
		buildData?.component_display_name ??
		buildData?.data?.component_display_name ??
		data?.component_display_name ??
		data?.display_name ??
		buildData?.display_name;
	const componentId =
		buildData?.component_id ??
		buildData?.componentId ??
		buildData?.node_id ??
		data?.node_id ??
		buildData?.id;

	let stepId = componentId || data?.id;
	if (!stepId) {
		stepId = "workflow-execution";
	}

	// If the server didn't send a display name, derive one from the vertex ID
	// so that humanLabel() can map it to a friendly string (e.g. "Chat Input" → "Receiving message")
	const resolvedTitle =
		componentDisplayName ??
		deriveComponentLabel(String(stepId));

	const durationMs =
		typeof buildData?.duration === "number"
			? buildData.duration
			: typeof buildData?.timedelta === "number"
				? buildData.timedelta
				: undefined;

	return {
		step_id: String(stepId),
		title: resolvedTitle || (status === "running" ? "Processing" : "Completed"),
		status,
		kind: "vertex",
		run_id: runId || "current-run",
		started_at: new Date(
			status === "completed" && typeof durationMs === "number" ? Date.now() - durationMs : Date.now(),
		).toISOString(),
		ended_at: status === "completed" ? new Date().toISOString() : undefined,
		duration_ms: status === "completed" ? durationMs : undefined,
	};
}

export function normalizeAgentStep(data: any): AgentStep | null {
	if (!data?.step_id || !data?.run_id) return null;

	return {
		...data,
		ended_at: data.ended_at ?? data.completed_at,
	};
}

export function buildInnLiveEventsUrl(
	hostUrl: string,
	eventsBase: string,
	sessionId: string,
	api_key?: string,
): string {
	const normalizedSessionId = encodeURIComponent((sessionId || "").trim());
	if (!normalizedSessionId) {
		return "";
	}

	const normalizedBase = (eventsBase || "").trim().replace(/\/+$/, "");
	if (!normalizedBase) {
		return "";
	}

	let url: string;
	if (/^https?:\/\//i.test(normalizedBase)) {
		url = `${normalizedBase}/${normalizedSessionId}/events`;
	} else {
		const normalizedHost = (hostUrl || "").trim().replace(/\/+$/, "");
		const normalizedPath = normalizedBase.startsWith("/") ? normalizedBase : `/${normalizedBase}`;
		url = `${normalizedHost}${normalizedPath}/${normalizedSessionId}/events`;
	}

	if (api_key) {
		const separator = url.includes("?") ? "&" : "?";
		url += `${separator}x-api-key=${encodeURIComponent(api_key.trim())}`;
	}

	return url;
}

/**
 * @param additional_headers - Headers as string (JSON), object, or undefined
 * @returns Parsed headers object, empty object {} if parsing fails, or undefined if input is undefined
 */
export function parseAdditionalHeaders(
	additional_headers?: { [key: string]: string } | string
): { [key: string]: string } | undefined {
	if (!additional_headers) return undefined;

	if (typeof additional_headers === 'string') {
		// Treat empty or whitespace-only strings as invalid
		if (additional_headers.trim() === '') {
			return {};
		}
		try {
			return JSON.parse(additional_headers);
		} catch (e) {
			console.error('Failed to parse additional_headers as JSON:', e, 'Value:', additional_headers);
			return {};
		}
	}

	// Already an object
	return additional_headers;
}
